import { css, Typography, useTheme } from '@mui/material'
import type { GetServerSideProps, NextPage } from 'next'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { NextHead } from 'components/Head/Head'
import Layout from 'components/Layout'
import Notification from 'components/Notification'
import { useHrefLang } from 'containers/seoPages/useHrefLang'
import i18nConfig from 'next-i18next.config'
import { apiClient } from 'services/api/client'
import { SendContactFormInput } from 'services/api/external/ExternalFormService'
import { unifiedColors } from 'styles/colors'
import { Theme, themeUtils } from 'styles/theme'
import { defaultFrontLocales } from 'utils/locale'

const Contact: NextPage = () => {
  const theme: Theme = useTheme()
  const { t } = useTranslation('contact')
  const hrefLangs = useHrefLang()
  const router = useRouter()

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<SendContactFormInput>()

  const [isSent, setIsSent] = useState(false)

  const onSubmit = async (input: SendContactFormInput) => {
    try {
      const isSaved = await apiClient.post<{ result: boolean }>(
        '/mail/sendContactForm',
        {
          ...input,
          language: router.locale
        }
      )
      setIsSent(isSaved.data.result)
    } catch (error) {
      setIsSent(false)
      console.error('Error sending contact form:', error)
    }
  }

  return (
    <div>
      <NextHead>
        <title>{t('contactUs')}</title>
        <meta name="description" content={t('contactDescription')} />
        <link rel="icon" href="/favicon.ico" />
        {...hrefLangs}
      </NextHead>
      <Layout rounded={false}>
        <Typography
          variant="h2"
          component="h1"
          css={css`
            margin: auto;
            margin-top: 32px;
            margin-bottom: 32px;
            width: 100%;
            max-width: 1440px;
            ${themeUtils.maxWidth.md} {
              padding: 0 16px;
            }
          `}
        >
          {t('contactUs')}
        </Typography>

        <main css={{ padding: '0 16px' }}>
          {isSent ? (
            <Notification
              success={isSent}
              successMessage={t('successMessage')}
            />
          ) : (
            <form
              onSubmit={handleSubmit(onSubmit)}
              css={css`
                display: grid;
                width: 100%;
                max-width: 1440px;
                margin: auto;
                margin-bottom: 64px;
                grid-template: repeat(2, 100px) 200px / 1fr 1fr;
                column-gap: 16px;
                row-gap: 8px;
                ${themeUtils.maxWidth.sm} {
                  row-gap: 0px;
                  grid-template: repeat(3, 80px) 170px / 1fr 1fr;
                }
                div {
                  display: flex;
                  flex-direction: column;
                }
                label {
                  ${themeUtils.label}
                  margin-bottom: 4px;
                }
                input {
                  border-radius: 6px;
                  &:focus,
                  &:target,
                  &:focus-within,
                  &:focus-visible,
                  &:active {
                    outline: none;
                    border: 2px solid ${unifiedColors.button.primary.default};
                  }
                  ${themeUtils.body};
                  padding: 12px;
                  ${themeUtils.maxWidth.sm} {
                    padding: 8px;
                  }
                }
                input:-webkit-autofill,
                input:-webkit-autofill:focus {
                  transition: background-color 600000s 0s, color 600000s 0s;
                  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0),
                    inset 0 0 0 100px rgba(255, 255, 255, 1);
                  ${themeUtils.body};
                }
                & input:-internal-autofill-selected {
                  background-color: white;
                }
                & input[type='submit'] {
                  font-weight: 700;
                  color: ${theme.palette.surface.default};
                }
                textarea {
                  border-radius: 6px;
                  height: 100px;
                  padding: 12px;
                  ${themeUtils.maxWidth.sm} {
                    padding: 8px;
                  }
                  resize: none;
                  ${themeUtils.body};
                  &:focus,
                  &:target,
                  &:focus-within,
                  &:focus-visible,
                  &:active {
                    outline: none;
                    border: 2px solid ${unifiedColors.button.primary.default};
                  }
                }

                & span {
                  ${themeUtils.bodySmall}
                  color: ${theme.palette.label.onRed};
                }
              `}
            >
              <div
                css={css`
                  grid-column: 1 / 2;
                  ${themeUtils.maxWidth.sm} {
                    grid-column: 1 / 3;
                  }
                `}
              >
                <label
                  css={css`
                    color: ${theme.palette.label.subduedDark};
                    ${errors.firstName &&
                    `color: ${theme.palette.label.error};`}
                  `}
                >
                  {t('firstName')}
                </label>
                <input
                  css={css`
                    border: 2px solid ${theme.palette.label.subduedDark};
                    ${errors.firstName &&
                    `border: 2px solid ${theme.palette.label.error} !important;;`}
                  `}
                  {...register('firstName', {
                    required: t('firstNameRequired')
                  })}
                />
                {errors.firstName && (
                  <span>{errors.firstName?.message?.toString()}</span>
                )}
              </div>
              <div
                css={css`
                  grid-column: 2 / 3;
                  ${themeUtils.maxWidth.sm} {
                    grid-column: 1 / 3;
                  }
                `}
              >
                <label
                  css={css`
                    color: ${theme.palette.label.subduedDark};
                    ${errors.lastName && `color: ${theme.palette.label.error};`}
                  `}
                >
                  {t('lastName')}
                </label>
                <input
                  css={css`
                    border: 2px solid ${theme.palette.label.subduedDark};
                    ${errors.lastName &&
                    `border: 2px solid ${theme.palette.label.error} !important;`}
                  `}
                  {...register('lastName', {
                    required: t('lastNameRequired')
                  })}
                />
                {errors.lastName && (
                  <span>{errors.lastName?.message?.toString()}</span>
                )}
              </div>
              <div
                css={css`
                  grid-column: 1 / 3;
                `}
              >
                <label
                  css={css`
                    color: ${theme.palette.label.subduedDark};
                    ${errors.email && `color: ${theme.palette.label.error};`}
                  `}
                >
                  {t('email')}
                </label>
                <input
                  css={css`
                    border: 2px solid ${theme.palette.label.subduedDark};
                    ${errors.email &&
                    `border: 2px solid ${theme.palette.label.error} !important;`}
                  `}
                  {...register('email', {
                    required: true,
                    pattern: /\S+@\S+\.\S+/
                  })}
                />
                {errors.email && <span>{t('emailRequired')}</span>}
              </div>
              <div
                css={css`
                  grid-column: 1 / 3;
                `}
              >
                <label
                  css={css`
                    color: ${theme.palette.label.subduedDark};
                    ${errors.message && `color: ${theme.palette.label.error};`}
                  `}
                >
                  {t('message')}
                </label>
                <textarea
                  css={css`
                    border: 2px solid ${theme.palette.label.subduedDark};
                    ${errors.message &&
                    `border: 2px solid ${theme.palette.label.error} !important;`}
                  `}
                  {...register('message', {
                    required: t('messageRequired')
                  })}
                />
                {errors.message && (
                  <span>{errors.message?.message?.toString()}</span>
                )}
              </div>

              <input
                type="submit"
                css={css`
                  ${themeUtils.button};
                  color: ${theme.palette.surface.default};
                  grid-column: 1 / 3;
                  cursor: pointer;
                  background-color: ${unifiedColors.button.primary.default};
                  border: none;
                  font-weight: 700;
                  height: 48px;
                  border-radius: 6px;
                  &:hover {
                    background-color: ${unifiedColors.button.primary.hovered};
                  }
                `}
                value={t('submit')}
              />
            </form>
          )}
        </main>
      </Layout>
    </div>
  )
}

export const getServerSideProps: GetServerSideProps = async ({ locale }) => {
  if (!locale) return { notFound: true }
  return {
    props: {
      ...(await serverSideTranslations(
        locale,
        [...defaultFrontLocales, 'contact'],
        i18nConfig
      ))
    }
  }
}

export default Contact
